import React, { useEffect, useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Divider,
} from '@mui/material';
import { Order, OrderItems } from '../../../store/order/api/interface';
import { getAssetURl, resolveAssetUrl } from '../../../utils/url';
import { IsNotNullOrEmpty } from '../../../utils/valid';
import { formatPlainPrice } from '../../../utils/formatNumber';
import {
  getCakeDisplayUnitPrice,
  getCakeRowDisplayTotal,
  getCakeUnitPrice,
  getOrderCakeInstructionForRow,
} from '../../../utils/orderTotals';
import { mergeImages } from '../../../utils/mergeImages';
import Loader from '../../../components/Loader';
import { DisplayMode } from '../../../enum';
import { useAppDispatch, useAppSelector } from '../../../store';
import { findSelectShape } from '../../../store/selectShape/api';
import { findSelectFlavorExport } from '../../../store/selectFlavorExport/api';
import { findSelectToppingsCategoryItemExport } from '../../../store/selectToppingCategoryItemExport/api';
import { MuiColorInput } from 'mui-color-input';
import {
  buildColorLabelByCode,
  getColorDisplayLabel,
} from '../../../utils/colorDisplay';

interface IProps {
  orderItem: OrderItems;
  key: string;
  idx: number;
  order: Order;
  handleOpenLightBox: (imageObject: any) => void;
  onOrderItemUpdate?: (orderItemId: string, updatedData: any) => void;
}

const TableRowViewOrderDetails: React.FC<IProps> = ({
  orderItem,
  idx,
  order,
  handleOpenLightBox,
  onOrderItemUpdate,
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [open, setOpen] = useState(false);
  const [mergedImageUrl, setMergedImageUrl] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    flavorId: orderItem?.order_cake?.flavorId || '',
    shapeId: orderItem?.order_cake?.shapeId || '',
    toppingId: orderItem?.order_cake?.toppingId || '',
    colorCode: orderItem?.order_cake?.colorCode || '',
    writeOnCake: orderItem?.order_cake?.writeOnCake || '',
  });
  
  // State for calculated price
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [priceBreakdown, setPriceBreakdown] = useState({
    shapePrice: 0,
    flavorPrice: 0,
    toppingPrice: 0,
    totalPrice: 0
  });
  
  // State for API data
  const [shapes, setShapes] = useState<any[]>([]);
  const [flavors, setFlavors] = useState<any[]>([]);
  const [toppings, setToppings] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const { data: paletteColors } = useAppSelector((state) => state.color);
  const colorLabelByCode = useMemo(
    () => buildColorLabelByCode(paletteColors),
    [paletteColors]
  );
  const savedColorCode = orderItem?.order_cake?.colorCode;
  const savedColorDisplayLabel = getColorDisplayLabel(
    savedColorCode,
    colorLabelByCode,
    '-'
  );

  // Parse image data from printImageUrl (may contain JSON with both images)
  const parseImageData = (printImageUrl: string | null | undefined) => {
    if (!printImageUrl) return { printImageUrl: null, customImageUrl: null };
    
    try {
      // Try to parse as JSON (new format with both images)
      const parsed = JSON.parse(printImageUrl);
      return {
        printImageUrl: parsed.printImageUrl || null,
        customImageUrl: parsed.customImageUrl || null,
      };
    } catch {
      // If not JSON, treat as old format (only printImageUrl)
      return {
        printImageUrl: printImageUrl,
        customImageUrl: null,
      };
    }
  };

  // Price calculation function based on backend logic
  const calculateCakePrice = (shape: any, flavor: any, topping: any, isPrintImage = false, printingCharges = 0) => {
    let price = 0.0;
    let shapePrice = 0;
    let flavorPrice = 0;
    let toppingPrice = 0;

    if (shape) {
      shapePrice = Number((shape?.discountedPrice || 0) > 0 ? shape?.discountedPrice : shape?.price || 0);
      price += shapePrice;

      if (flavor) {
        flavorPrice = Number((flavor?.pricePerKg || 0) * (shape?.kg || 0));
        price += flavorPrice;
      }
    }

    if (topping) {
      toppingPrice = Number((topping?.discountedPrice || 0) > 0 ? topping?.discountedPrice : topping?.price || 0);
      price += toppingPrice;
    }

    if (isPrintImage) {
      price += Number(printingCharges || 0);
    }

    return {
      shapePrice: Number(shapePrice),
      flavorPrice: Number(flavorPrice),
      toppingPrice: Number(toppingPrice),
      totalPrice: Number(price)
    };
  };

  // Calculate price when data changes
  const updatePriceCalculation = (data: any) => {
    const selectedShape = shapes.find(shape => shape.id === data.shapeId);
    const selectedFlavor = flavors.find(flavor => flavor.id === data.flavorId);
    const selectedTopping = toppings.find(topping => topping.id === data.toppingId);
    
    const priceData = calculateCakePrice(selectedShape, selectedFlavor, selectedTopping);
    
    // Ensure all values are numbers
    const safePriceData = {
      shapePrice: Number(priceData.shapePrice || 0),
      flavorPrice: Number(priceData.flavorPrice || 0),
      toppingPrice: Number(priceData.toppingPrice || 0),
      totalPrice: Number(priceData.totalPrice || 0)
    };
    
    setCalculatedPrice(safePriceData.totalPrice);
    setPriceBreakdown(safePriceData);
  };

  useEffect(() => {
    const itemCount = order?.order_items?.length ?? 0;
    if (!itemCount) {
      setMergedImageUrl([]);
      return;
    }

    const fetchMergedImage = async () => {
      try {
        const allMergedImages = await Promise.all(
          order?.order_items?.map(async (item) => {
            if (
              IsNotNullOrEmpty(item?.order_cake) &&
              item?.order_cake?.displayMode === DisplayMode.front
            ) {
              if (
                item?.order_cake?.shape?.frontImg &&
                item?.order_cake?.flavor?.shapeImages?.[0]?.front
                // &&
                // item?.order_cake?.topping_category_item?.toppingShapeImages?.[0]
                //   ?.front
              ) {
                const url = await mergeImages(
                  getAssetURl(item?.order_cake?.shape?.frontImg, assetUrl),
                  getAssetURl(
                    item?.order_cake?.flavor?.shapeImages?.[0]?.front,
                    assetUrl
                  ),
                  item?.order_cake?.colorCode,
                  getAssetURl(
                    item?.order_cake?.topping_category_item
                      ?.toppingShapeImages?.[0]?.front,
                    assetUrl
                  ),
                  getAssetURl(item?.order_cake?.shape?.frontColorImg, assetUrl)
                );
                return url;
              }
            } else if (
              IsNotNullOrEmpty(item?.order_cake) &&
              item?.order_cake?.displayMode === DisplayMode.top
            ) {
              if (
                item?.order_cake?.shape?.topImg &&
                item?.order_cake?.flavor?.shapeImages?.[0]?.top
                // &&
                // item?.order_cake?.topping_category_item?.toppingShapeImages?.[0]
                //   ?.top
              ) {
                const url = await mergeImages(
                  getAssetURl(item?.order_cake?.shape?.topImg, assetUrl),
                  getAssetURl(
                    item?.order_cake?.flavor?.shapeImages?.[0]?.top,
                    assetUrl
                  ),
                  item?.order_cake?.colorCode,
                  getAssetURl(
                    item?.order_cake?.topping_category_item
                      ?.toppingShapeImages?.[0]?.top,
                    assetUrl
                  ),
                  getAssetURl(item?.order_cake?.shape?.topColorImg, assetUrl)
                );
                return url;
              }
            } else {
              return null;
            }
            return null;
          })
        );
        setMergedImageUrl(allMergedImages.map((image) => image ?? null));
      } catch (error) {
        console.error(error);
        setMergedImageUrl(Array(itemCount).fill(null));
      }
    };

    fetchMergedImage();
  }, [order]);

  // Fetch shapes, flavors, and toppings data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        // Fetch shapes
        const shapesResponse = await dispatch(findSelectShape({
          limit: 100,
          skip: 0,
        })).unwrap();
        setShapes(shapesResponse?.data || []);

        // Fetch flavors
        const flavorsResponse = await dispatch(findSelectFlavorExport({
          isExport: true,
        })).unwrap();
        setFlavors(flavorsResponse?.data || []);

        // Fetch toppings
        const toppingsResponse = await dispatch(findSelectToppingsCategoryItemExport({
          isExport: true,
          limit: 100,
          skip: 0,
        })).unwrap();
        setToppings(toppingsResponse?.data || []);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Update image when data is loaded and we have the necessary information
  useEffect(() => {
    if (shapes.length > 0 && flavors.length > 0 && toppings.length > 0 && !isLoadingData) {
      updateMergedImage(editedData);
    }
  }, [shapes, flavors, toppings, isLoadingData, editedData]);

  // Calculate initial price when shapes, flavors, and toppings are loaded
  useEffect(() => {
    if (shapes.length > 0 && flavors.length > 0 && toppings.length > 0) {
      updatePriceCalculation(editedData);
    }
  }, [shapes, flavors, toppings, editedData.shapeId, editedData.flavorId, editedData.toppingId]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (onOrderItemUpdate) {
      // Find the selected items to get their labels
      const selectedShape = shapes.find(shape => shape.id === editedData.shapeId);
      const selectedFlavor = flavors.find(flavor => flavor.id === editedData.flavorId);
      const selectedTopping = toppings.find(topping => topping.id === editedData.toppingId);
      
      // Get current (old) names
      const oldShapeName = orderItem?.order_cake?.shapeLabelEn || orderItem?.order_cake?.shape?.labelEn || 'Unknown Shape';
      const oldFlavorName = orderItem?.order_cake?.flavorLabelEn || orderItem?.order_cake?.flavor?.labelEn || 'Unknown Flavor';
      const oldToppingName = orderItem?.order_cake?.topping_category_item?.labelEn || 'None';
      
      // Get new names
      const newShapeName = selectedShape?.labelEn || oldShapeName;
      const newFlavorName = selectedFlavor?.labelEn || oldFlavorName;
      const newToppingName = selectedTopping?.labelEn || 'None';
      
      // Prepare complete data with labels (fallback to existing data if selected items don't have labels)
      const completeData = {
        ...editedData,
        shapeLabelEn: selectedShape?.labelEn || orderItem?.order_cake?.shapeLabelEn || orderItem?.order_cake?.shape?.labelEn || '',
        shapeLabelAr: selectedShape?.labelAr || orderItem?.order_cake?.shapeLabelAr || orderItem?.order_cake?.shape?.labelAr || '',
        flavorLabelEn: selectedFlavor?.labelEn || orderItem?.order_cake?.flavorLabelEn || orderItem?.order_cake?.flavor?.labelEn || '',
        flavorLabelAr: selectedFlavor?.labelAr || orderItem?.order_cake?.flavorLabelAr || orderItem?.order_cake?.flavor?.labelAr || '',
        displayMode: orderItem?.order_cake?.displayMode || 'front',
        categoryId: (orderItem?.order_cake as any)?.categoryId || '',
        categoryCakeId: (orderItem?.order_cake as any)?.categoryCakeId || '',
        // Include calculated price for order item update
        calculatedPrice: calculatedPrice,
        // Include names for logging
        names: {
          oldShapeName,
          newShapeName,
          oldFlavorName,
          newFlavorName,
          oldToppingName,
          newToppingName,
          userFullName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown User',
          userEmail: user?.email || 'Unknown Email'
        }
      };
      
      onOrderItemUpdate(orderItem.id, completeData);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedData({
      flavorId: orderItem?.order_cake?.flavorId || '',
      shapeId: orderItem?.order_cake?.shapeId || '',
      toppingId: orderItem?.order_cake?.toppingId || '',
      colorCode: orderItem?.order_cake?.colorCode || '',
      writeOnCake: orderItem?.order_cake?.writeOnCake || '',
    });
    setIsEditing(false);
  };

  const handleFieldChange = (field: string, value: any) => {
    const newData = {
      ...editedData,
      [field]: value
    };
    
    setEditedData(newData);
    
    // Update merged image when any field changes
    updateMergedImage(newData);
    
    // Update price calculation when shape, flavor, or topping changes
    if (field === 'shapeId' || field === 'flavorId' || field === 'toppingId') {
      updatePriceCalculation(newData);
    }
  };

  // Function to update merged image based on current data
  const updateMergedImage = async (data: any) => {
    try {
      console.log('updateMergedImage called with:', data);
      
      if (!data.shapeId || !orderItem?.order_cake?.displayMode) {
        console.log('Missing required data:', { shapeId: data.shapeId, displayMode: orderItem?.order_cake?.displayMode });
        return;
      }

      // Find the selected shape, flavor, and topping from fetched data
      const selectedShape = shapes.find(shape => shape.id === data.shapeId);
      const selectedFlavor = flavors.find(flavor => flavor.id === data.flavorId);
      const selectedTopping = toppings.find(topping => topping.id === data.toppingId);

      console.log('Selected items:', { selectedShape, selectedFlavor, selectedTopping });

      if (!selectedShape) {
        console.warn('Shape not found for image update');
        return;
      }

      // Get URLs based on display mode
      let shapeUrl = '', flavorUrl = '', toppingUrl = '', colorImgUrl = '';
      
      if (orderItem?.order_cake?.displayMode === DisplayMode.front) {
        // Front view
        if (selectedShape?.frontImg) {
          shapeUrl = getAssetURl(selectedShape.frontImg, assetUrl);
        }
        
        if (selectedFlavor?.shapeImages) {
          const flavorShape = selectedFlavor.shapeImages.find(
            (img: any) => img?.shapeId === data.shapeId
          );
          if (flavorShape?.front) {
            flavorUrl = getAssetURl(flavorShape.front, assetUrl);
          }
        }
        
        if (selectedTopping?.toppingShapeImages) {
          const toppingShape = selectedTopping.toppingShapeImages.find(
            (img: any) => img?.shapeId === data.shapeId
          );
          if (toppingShape?.front) {
            toppingUrl = getAssetURl(toppingShape.front, assetUrl);
          }
        }
        
        if (selectedShape?.frontColorImg) {
          colorImgUrl = getAssetURl(selectedShape.frontColorImg, assetUrl);
        }
      } else {
        // Top view
        if (selectedShape?.topImg) {
          shapeUrl = getAssetURl(selectedShape.topImg, assetUrl);
        }
        
        if (selectedFlavor?.shapeImages) {
          const flavorShape = selectedFlavor.shapeImages.find(
            (img: any) => img?.shapeId === data.shapeId
          );
          if (flavorShape?.top) {
            flavorUrl = getAssetURl(flavorShape.top, assetUrl);
          }
        }
        
        if (selectedTopping?.toppingShapeImages) {
          const toppingShape = selectedTopping.toppingShapeImages.find(
            (img: any) => img?.shapeId === data.shapeId
          );
          if (toppingShape?.top) {
            toppingUrl = getAssetURl(toppingShape.top, assetUrl);
          }
        }
        
        if (selectedShape?.topColorImg) {
          colorImgUrl = getAssetURl(selectedShape.topColorImg, assetUrl);
        }
      }

      // Generate merged image
      if (shapeUrl) {
        console.log('Generating merged image with URLs:', { shapeUrl, flavorUrl, toppingUrl, colorImgUrl, colorCode: data.colorCode });
        
        const merged = await mergeImages(
          shapeUrl,
          flavorUrl || '',
          data.colorCode || '',
          toppingUrl || '',
          colorImgUrl || ''
        );
        
        console.log('Merged image result:', merged);
        
        if (merged) {
          setMergedImageUrl(prev => {
            const newArray = [...prev];
            newArray[idx] = merged;
            console.log('Updated mergedImageUrl array:', newArray);
            return newArray;
          });
        }
      } else {
        console.warn('No shape URL available for image generation');
      }
    } catch (error) {
      console.error('Error updating merged image:', error);
    }
  };

  const rowTotal = getCakeRowDisplayTotal(
    orderItem?.qty,
    orderItem?.unitPrice,
    orderItem?.order_cake
  );
  const rowUnitPrice = getCakeDisplayUnitPrice(
    orderItem?.unitPrice,
    orderItem?.order_cake
  );

  return (
    <React.Fragment>
      <>
          <TableRow
            key={idx}
            sx={{
              borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
            }}
          >
            <TableCell align='left'>
              {IsNotNullOrEmpty(orderItem?.order_cake) && (
                <IconButton
                  aria-label='expand row'
                  size='small'
                  onClick={() => setOpen(!open)}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              )}
            </TableCell>

            <TableCell>{idx + 1}</TableCell>

            <TableCell align='left'>
              {orderItem?.addon_product?.previewUrl && (
                <Avatar
                  onClick={() =>
                    handleOpenLightBox(orderItem?.addon_product?.previewUrl)
                  }
                  imgProps={{ crossOrigin: 'anonymous' }}
                  src={getAssetURl(
                    orderItem?.addon_product?.previewUrl,
                    assetUrl
                  )}
                  sx={{
                    borderRadius: 'unset',
                    m: 1,
                    objectFit: 'contain',
                    '& img': { objectFit: 'contain' },
                    cursor: 'pointer',
                  }}
                />
              )}
              {mergedImageUrl[idx] && (
                <Avatar
                  onClick={() => handleOpenLightBox(mergedImageUrl[idx])}
                  imgProps={{ crossOrigin: 'anonymous' }}
                  src={mergedImageUrl[idx]}
                  sx={{
                    borderRadius: 'unset',
                    m: 1,
                    objectFit: 'contain',
                    '& img': { objectFit: 'contain' },
                    cursor: 'pointer',
                  }}
                />
              )}
            </TableCell>
            <TableCell align='right'>
              {(() => {
                const imageData = parseImageData(orderItem?.order_cake?.printImageUrl);
                return imageData.customImageUrl ? (
                <Avatar
                    onClick={() => handleOpenLightBox(imageData.customImageUrl)}
                  imgProps={{ crossOrigin: 'anonymous' }}
                    src={resolveAssetUrl(imageData.customImageUrl, assetUrl)}
                  sx={{
                    borderRadius: 'unset',
                    m: 1,
                    objectFit: 'contain',
                    '& img': { objectFit: 'contain' },
                    cursor: 'pointer',
                  }}
                />
              ) : (
                '-'
                );
              })()}
            </TableCell>
            <TableCell align='right'>
              {(() => {
                const imageData = parseImageData(orderItem?.order_cake?.printImageUrl);
                return imageData.printImageUrl ? (
                  <Avatar
                    onClick={() => handleOpenLightBox(imageData.printImageUrl)}
                    imgProps={{ crossOrigin: 'anonymous' }}
                    src={resolveAssetUrl(imageData.printImageUrl, assetUrl)}
                    sx={{
                      borderRadius: 'unset',
                      m: 1,
                      objectFit: 'contain',
                      '& img': { objectFit: 'contain' },
                      cursor: 'pointer',
                    }}
                  />
                ) : (
                  '-'
                );
              })()}
            </TableCell>
            <TableCell align='right'>
              {orderItem?.order_cake
                ? `${orderItem?.order_cake?.shapeLabelEn} (${orderItem?.order_cake?.flavorLabelEn})`
                : '-'}
            </TableCell>
            <TableCell align='right'>
              {orderItem?.addon_product
                ? `${orderItem?.addon_product?.labelEn} `
                : '-'}
            </TableCell>
            <TableCell align='right'>
              {getOrderCakeInstructionForRow(orderItem)}
            </TableCell>
            <TableCell align='right'>
              {orderItem?.order_cake
                ? orderItem?.order_cake?.writeOnCake || '-'
                : '-'}
            </TableCell>
            <TableCell align='right'>{orderItem?.qty}</TableCell>
            <TableCell align='right'>{rowUnitPrice}</TableCell>
            <TableCell align='right'>{rowTotal}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              style={{ paddingBottom: 0, paddingTop: 0 }}
              colSpan={12}
            >
              <Collapse
                in={open}
                timeout='auto'
                unmountOnExit
              >
                <Box>
                  <Table
                    size='small'
                    aria-label='purchases'
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell align='center'>{'Type'}</TableCell>
                        <TableCell align='center'>{'Name'}</TableCell>
                        <TableCell align='center'>{'Image'}</TableCell>
                        <TableCell align='center'>{'Price'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow key={orderItem?.order_cake?.flavorId}>
                        <TableCell align='center'>Flavor</TableCell>
                        <TableCell align='center'>
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <InputLabel>Select Flavor</InputLabel>
                              <Select
                                value={editedData.flavorId}
                                onChange={(e) => handleFieldChange('flavorId', e.target.value)}
                                label="Select Flavor"
                                disabled={isLoadingData}
                              >
                                {isLoadingData ? (
                                  <MenuItem disabled>
                                    <Loader />
                                  </MenuItem>
                                ) : (
                                  flavors.map((flavor) => (
                                    <MenuItem key={flavor.id} value={flavor.id}>
                                      {flavor.labelEn} ({flavor.labelAr})
                                    </MenuItem>
                                  ))
                                )}
                              </Select>
                            </FormControl>
                          ) : (
                            `${orderItem?.order_cake?.flavor?.labelEn} (${orderItem?.order_cake?.flavor?.labelAr})`
                          )}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {(() => {
                            const currentFlavor = flavors.find(flavor => flavor.id === editedData.flavorId) || orderItem?.order_cake?.flavor;
                            return currentFlavor?.previewUrl && (
                              <Avatar
                                onClick={() =>
                                  handleOpenLightBox(
                                    currentFlavor?.previewUrl
                                  )
                                }
                                imgProps={{ crossOrigin: 'anonymous' }}
                                src={getAssetURl(
                                  currentFlavor?.previewUrl,
                                  assetUrl
                                )}
                                sx={{
                                  borderRadius: 'unset',
                                  m: 1,
                                  objectFit: 'contain',
                                  '& img': { objectFit: 'contain' },
                                  cursor: 'pointer',
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                      <TableRow key={orderItem?.order_cake?.shapeId}>
                        <TableCell align='center'>Shape</TableCell>
                        <TableCell align='center'>
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <InputLabel>Select Shape</InputLabel>
                              <Select
                                value={editedData.shapeId}
                                onChange={(e) => handleFieldChange('shapeId', e.target.value)}
                                label="Select Shape"
                                disabled={isLoadingData}
                              >
                                {isLoadingData ? (
                                  <MenuItem disabled>
                                    <Loader />
                                  </MenuItem>
                                ) : (
                                  shapes.map((shape) => (
                                    <MenuItem key={shape.id} value={shape.id}>
                                      {shape.labelEn} ({shape.labelAr})
                                    </MenuItem>
                                  ))
                                )}
                              </Select>
                            </FormControl>
                          ) : (
                            `${orderItem?.order_cake?.shape?.labelEn} (${orderItem?.order_cake?.shape?.labelAr})`
                          )}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {(() => {
                            const currentShape = shapes.find(shape => shape.id === editedData.shapeId) || orderItem?.order_cake?.shape;
                            return currentShape?.previewUrl && (
                              <Avatar
                                onClick={() =>
                                  handleOpenLightBox(
                                    currentShape?.previewUrl
                                  )
                                }
                                imgProps={{ crossOrigin: 'anonymous' }}
                                src={getAssetURl(
                                  currentShape?.previewUrl,
                                  assetUrl
                                )}
                                sx={{
                                  borderRadius: 'unset',
                                  m: 1,
                                  objectFit: 'contain',
                                  '& img': { objectFit: 'contain' },
                                  cursor: 'pointer',
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                      {orderItem?.order_cake?.toppingId && (
                        <TableRow key={orderItem?.order_cake?.toppingId}>
                          <TableCell align='center'>Toppings</TableCell>
                          <TableCell align='center'>
                            {isEditing ? (
                              <FormControl fullWidth size="small">
                                <InputLabel>Select Toppings</InputLabel>
                                <Select
                                  value={editedData.toppingId}
                                  onChange={(e) => handleFieldChange('toppingId', e.target.value)}
                                  label="Select Toppings"
                                  disabled={isLoadingData}
                                >
                                  {isLoadingData ? (
                                    <MenuItem disabled>
                                      <Loader />
                                    </MenuItem>
                                  ) : (
                                    toppings.map((topping) => (
                                      <MenuItem key={topping.id} value={topping.id}>
                                        {topping.labelEn} ({topping.labelAr})
                                      </MenuItem>
                                    ))
                                  )}
                                </Select>
                              </FormControl>
                            ) : (
                              `${orderItem?.order_cake?.topping_category_item?.labelEn} (${orderItem?.order_cake?.topping_category_item?.labelAr}) ${orderItem?.order_cake?.topping_category_item?.deliveryDayGap === 0 ? '(Fast Delivery)' : ''}`
                            )}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{ display: 'flex', justifyContent: 'center' }}
                          >
                            {(() => {
                              const currentTopping = toppings.find(topping => topping.id === editedData.toppingId) || orderItem?.order_cake?.topping_category_item;
                              return currentTopping?.previewUrl && (
                                <Avatar
                                  onClick={() =>
                                    handleOpenLightBox(
                                      currentTopping?.previewUrl
                                    )
                                  }
                                  imgProps={{ crossOrigin: 'anonymous' }}
                                  src={getAssetURl(
                                    currentTopping?.previewUrl,
                                    assetUrl
                                  )}
                                  sx={{
                                    borderRadius: 'unset',
                                    m: 1,
                                    objectFit: 'contain',
                                    '& img': { objectFit: 'contain' },
                                    cursor: 'pointer',
                                  }}
                                />
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow key={orderItem?.order_cake?.colorCode}>
                        <TableCell align='center'>Color</TableCell>
                        <TableCell align='center'>
                          {isEditing ? (
                            <MuiColorInput
                              label="Select Color"
                              format="hex"
                              value={editedData.colorCode || '#ffffff'}
                              onChange={(newColor: string) => handleFieldChange('colorCode', newColor)}
                              size="small"
                              sx={{ width: 150 }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <span>{savedColorDisplayLabel}</span>
                              <div
                                style={{
                                  width: 24,
                                  height: 24,
                                  backgroundColor: savedColorCode || '#ffffff',
                                  borderRadius: '50%',
                                  border: '1px solid #ccc',
                                }}
                              />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <div
                              style={{
                                width: 18,
                                height: 18,
                                backgroundColor: editedData.colorCode || orderItem?.order_cake?.colorCode,
                                marginLeft: 8,
                                borderRadius: '50%',
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow key="write-on-cake">
                        <TableCell align='center'>Write on Cake</TableCell>
                        <TableCell align='center'>
                          {isEditing ? (
                            <TextField
                              size="small"
                              fullWidth
                              value={editedData.writeOnCake}
                              onChange={(e) => handleFieldChange('writeOnCake', e.target.value)}
                              placeholder="Enter text to write on cake"
                              multiline
                              rows={2}
                            />
                          ) : (
                            orderItem?.order_cake?.writeOnCake || '-'
                          )}
                        </TableCell>
                        <TableCell align='center'>-</TableCell>
                      </TableRow>

                      <TableRow key="premium-packaging">
                        <TableCell align='center'>Premium Packaging</TableCell>
                        <TableCell align='center'>
                          {orderItem?.order_cake?.cakeBox?.labelEn ||
                            (orderItem?.order_cake as any)?.cake_box?.labelEn ||
                            '-'}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {(() => {
                            const cakeBoxImgUrl =
                              orderItem?.order_cake?.cakeBox?.imgUrl ||
                              (orderItem?.order_cake as any)?.cake_box?.imgUrl;
                            if (!cakeBoxImgUrl) return '-';
                            const cakeBoxSrc = getAssetURl(
                              cakeBoxImgUrl,
                              assetUrl
                            );
                            return (
                              <Avatar
                                variant='rounded'
                                onClick={() =>
                                  handleOpenLightBox(cakeBoxImgUrl)
                                }
                                src={cakeBoxSrc}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  m: 1,
                                  cursor: 'pointer',
                                  '& img': { objectFit: 'contain' },
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell align='center'>
                          {orderItem?.order_cake?.cakeBox ||
                          (orderItem?.order_cake as any)?.cake_box
                            ? formatPlainPrice(
                                (
                                  orderItem?.order_cake?.cakeBox ||
                                  (orderItem?.order_cake as any)?.cake_box
                                )?.price ?? 0
                              )
                            : '-'}
                        </TableCell>
                      </TableRow>

                      <TableRow key="cake-line-price">
                        <TableCell align='center'>Cake</TableCell>
                        <TableCell align='center'>
                          {orderItem?.order_cake?.shapeLabelEn} (
                          {orderItem?.order_cake?.flavorLabelEn})
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {mergedImageUrl[idx] ? (
                            <Avatar
                              onClick={() =>
                                handleOpenLightBox(mergedImageUrl[idx])
                              }
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={mergedImageUrl[idx]}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                                cursor: 'pointer',
                              }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {getCakeUnitPrice(orderItem?.unitPrice)}
                        </TableCell>
                      </TableRow>

                      {/* Price Breakdown Section */}
                      <TableRow key="price-breakdown">
                        <TableCell align='center' colSpan={3}>
                          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="h6" align="center" gutterBottom>
                              Price Breakdown
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">Shape Price:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {Number(priceBreakdown.shapePrice || 0).toFixed(2)} QAR
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">Flavor Price:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {Number(priceBreakdown.flavorPrice || 0).toFixed(2)} QAR
                                </Typography>
                              </Box>
                              {Number(priceBreakdown.toppingPrice || 0) > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2">Topping Price:</Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {Number(priceBreakdown.toppingPrice || 0).toFixed(2)} QAR
                                  </Typography>
                                </Box>
                              )}
                              <Divider />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6">Total Cake Price:</Typography>
                                <Typography variant="h6" color="primary" fontWeight="bold">
                                  {Number(calculatedPrice || 0).toFixed(2)} QAR
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">Current Order Price:</Typography>
                                <Typography variant="body2" color={calculatedPrice !== parseFloat(orderItem?.unitPrice || '0') ? 'error' : 'success'}>
                                  {parseFloat(orderItem?.unitPrice || '0').toFixed(2)} QAR
                                </Typography>
                              </Box>
                              {calculatedPrice !== parseFloat(orderItem?.unitPrice || '0') && (
                                <Typography variant="caption" color="error" align="center">
                                  Price will be updated when you save changes
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
                
                {/* Single Save/Cancel buttons */}
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  {isEditing ? (
                    <>
                      <Button variant="contained" onClick={handleSave} size="medium">
                        Save Changes
                      </Button>
                      <Button variant="outlined" onClick={handleCancel} size="medium">
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button variant="outlined" onClick={handleEdit} size="medium">
                      Edit Cake Details
                    </Button>
                  )}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        </>
    </React.Fragment>
  );
};

export default TableRowViewOrderDetails;
